import React from 'react'
import styled from '@emotion/styled'
import { useExpandToggle } from '@mobi/utils/hooks/useExpandToggle'
import { PillButton } from '@mobi/component-library/Base/Buttons/PillButton'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { shadow, spacing } from '@mobi/component-library/Foundations'

interface Props {
  /** truncate children to this height if they exceed it */
  maxHeightRem: number
  children: React.ReactNode
  /** override button text */
  showMoreText?: React.ReactNode
  /** override button text if shouldAllowClose is true */
  showLessText?: React.ReactNode
  onChangeCallback?: (expanded: boolean) => void
}

export const HeightTruncate: React.FC<Props> = ({
  maxHeightRem,
  children,
  showMoreText,
  showLessText,
  onChangeCallback,
}) => {
  const [shouldTruncate, setShouldTruncate] = React.useState(false)
  const maxHeightPx = React.useMemo(() => convertRemToPixels(maxHeightRem), [maxHeightRem])

  const { wrapperRef, contentRef, shouldRender, isExpanded, toggleExpanded } = useExpandToggle({
    timeoutMs: 400,
    shouldStartExpanded: false,
  })

  React.useEffect(() => {
    if (!contentRef.current) return
    setShouldTruncate(contentRef.current.clientHeight > maxHeightPx)
  }, [contentRef, maxHeightPx])

  const isOpen = isExpanded || shouldRender

  React.useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return
    wrapperRef.current.style.maxHeight = isOpen ? '' : maxHeightPx + 'px'
    contentRef.current.style.paddingBottom = isOpen ? '6.2rem' : '0rem'
  }, [isOpen, maxHeightPx, wrapperRef, contentRef])

  React.useLayoutEffect(() => {
    if (!wrapperRef.current || !contentRef.current) {
      return
    }
    wrapperRef.current.style.minHeight =
      Math.min(contentRef.current.clientHeight, maxHeightPx) + 'px'
  }, [maxHeightPx, contentRef, wrapperRef])

  const handleToggleExpanded = () => {
    toggleExpanded(!isOpen)
    onChangeCallback?.(!isOpen)
  }

  return (
    <WrapperStyled ref={wrapperRef}>
      <div ref={contentRef}>{children}</div>

      {shouldTruncate && (
        <span
          className={`height-truncate__control ${isExpanded ? 'height-truncate__control--open' : ''}`}
        >
          <PillButton
            size='sm'
            color='secondary_outline'
            data-testid='height-truncate-toggle'
            onClick={handleToggleExpanded}
          >
            {!isExpanded
              ? showMoreText || (
                  <>
                    Show More <Icon name='SolidChevronDown' size='2rem' />
                  </>
                )
              : showLessText || (
                  <>
                    Show Less <Icon name='SolidChevronUp' size='2rem' />
                  </>
                )}
          </PillButton>
        </span>
      )}
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function convertRemToPixels(rem: number) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize || '10')
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  transition: 'height 0.4s ease',
  overflow: 'hidden',
  position: 'relative',

  '.height-truncate__control': {
    zIndex: 1,
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: '6rem',
    paddingBottom: spacing.md,

    '> button': {
      boxShadow: shadow.md,
      zIndex: 1,
      position: 'relative',
    },

    '::after': {
      content: '""',
      position: 'absolute',
      zIndex: 0,
      left: 0,
      right: 0,
      bottom: 0,
      top: 0,
      pointerEvents: 'none',
      opacity: 1,
      transform: 'translateY(0)',
      transition: 'opacity 0.8s ease, transform 0.4s ease',
      background: 'linear-gradient(0deg,rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 99%)',
    },

    '&--open': {
      '::after': {
        opacity: 0,
        transform: 'translateY(6rem)',
      },
    },
  },
})
